/* eslint-disable jsx-a11y/no-static-element-interactions */
import Button from '@/components/Button';
import { LockClosedIcon, PlayIcon } from '@heroicons/react/24/solid';
import { UploadFileEntityResponse } from '@/graphql/generated/graphql';
import { ArrowPathIcon } from '@heroicons/react/24/outline';
import { ReactNode } from 'react';
import DefaultContentTemplateTags from '@/components/DefaultContentTemplate/Tags';
import { Link, useNavigate } from 'react-router-dom';
import { useResponsiveImage } from '@/utils/useResponsiveImage';
import classNames from 'classnames';

type LessonCardProps = {
  title: string;
  description: string;
  isLocked?: boolean;
  slug?: string;
  thumbnail?: UploadFileEntityResponse;
  url: string;
  done?: boolean;
  lockedLabel?: string;
  watchAgainLabel?: string;
  watchLabel?: string;
  progress?: number;
  descriptionClasses?: string;
  button?: ReactNode;
  favorite?: ReactNode;
  tag?: string;
  duration?: string;
  onClick?: () => void;
  truncateDescription?: boolean;
  onClickButton?: () => void;
  noImage?: boolean;
};

const LessonCard = ({
  title,
  description,
  isLocked = false,
  thumbnail,
  noImage = false,
  url,
  done,
  progress,
  watchLabel = 'WATCH LESSON',
  watchAgainLabel = 'WATCH AGAIN',
  lockedLabel = 'LOCKED',
  button,
  favorite,
  descriptionClasses,
  tag,
  duration,
  onClick,
  truncateDescription = true,
  onClickButton,
}: LessonCardProps) => {
  const navigate = useNavigate();
  const { src } = useResponsiveImage(thumbnail);

  return (
    <div
      className={classNames('border-1 grid grid-cols-1 overflow-hidden rounded-lg border border-primary-500 bg-white', {
        'md:min-h-[212px] md:grid-cols-[218px_1fr]': !noImage,
      })}>
      {thumbnail && !noImage && (
        <div
          className={classNames('min-h-[212px] w-full bg-cover bg-center', onClick ? 'cursor-pointer' : '')}
          onClick={onClick}
          style={{ backgroundImage: `url('${src}')` }}
        />
      )}
      <div className="flex flex-col gap-sh-15 rounded-b-lg px-5 py-5 md:rounded-b-none md:rounded-r-lg">
        <div>
          {tag && duration && (
            <DefaultContentTemplateTags duration={duration || ''} tags={[tag || '']} className="!mb-sh-15 !mt-0" />
          )}
          <div className="flex justify-between gap-5">
            <Link to={url}>
              <h2 className="line-clamp-1 font-larken text-sh-18-28">{title}</h2>
            </Link>
            {favorite}
          </div>
          <Link to={url}>
            <p
              className={classNames(
                'text-sh-xxs text-black',
                descriptionClasses || '',
                truncateDescription ? 'line-clamp-1' : '',
              )}
              title={description}>
              {description}
            </p>
          </Link>
          {progress && progress > 0 ? (
            <div className="mt-4 h-[6px] w-full rounded-full bg-[#2073D333]/20">
              <div
                className="h-[6px] rounded-full bg-[#488FE3]"
                style={{ width: `${progress <= 100 ? progress : 100}%` }}></div>
            </div>
          ) : null}
        </div>
        <div>
          {!button ? (
            <Button
              disabled={isLocked}
              theme={done ? 'outline' : 'solid'}
              className="flex max-w-fit items-center"
              onClick={() => {
                if (onClickButton) {
                  onClickButton();
                }
                navigate(url);
              }}
              href={url}>
              {isLocked ? (
                <>
                  <LockClosedIcon className="inline-block h-4 w-4" />
                  {lockedLabel}
                </>
              ) : (
                <>
                  {done ? (
                    <ArrowPathIcon className="inline-block h-4 w-4" />
                  ) : (
                    <PlayIcon className="inline-block h-4 w-4" />
                  )}
                  {done ? watchAgainLabel : watchLabel}
                </>
              )}
            </Button>
          ) : (
            button
          )}
        </div>
      </div>
    </div>
  );
};

export default LessonCard;
